/**
 * Rescales the cart summary's promo shipping amounts for the active presentment
 * currency. The progress bar converts itself in assets/shipping-bar.js, which
 * also has to move it as the cart changes.
 *
 * `shipping_rate` / `shipping_free_threshold` are theme settings — plain numbers in
 * shop currency — while cart prices already reach Liquid converted. Liquid has no
 * exchange rate, so the section renders the shop-currency numbers and this script
 * rescales them with `Shopify.currency.rate` (shop -> active presentment).
 *
 * Converted amounts are ceiled to a whole currency unit, so a rate-derived 344.87
 * shows as 345. assets/shipping-bar.js ceils the same way, so the charge and the
 * progress bar's threshold stay consistent with each other.
 *
 * A no-op whenever the rate is 1, which covers every request served in the shop
 * currency — including markets whose own currency is the shop currency, where the
 * per-market settings in templates/cart.context.<market>.json are already exact.
 *
 * Classic deferred script, not a module: it sits in the section body, which
 * precedes the importmap in snippets/scripts.liquid — same constraint as
 * cart-page.js. Re-runs on `cart:update`, since that replaces `.js-contents`.
 */

(() => {
  const shopifyCurrency = window.Shopify && window.Shopify.currency;
  const conversionRate = Number(shopifyCurrency && shopifyCurrency.rate);
  if (!Number.isFinite(conversionRate) || conversionRate <= 0 || conversionRate === 1) return;

  // A live FX rate lands on amounts like 344.87, which reads as a bug next to a
  // hand-picked promo price. Round up to the next whole unit so the figure looks
  // deliberate and the charge is never understated. Raise to 1000 for the nearest
  // 10, 10000 for the nearest 100.
  const CEIL_TO_MINOR_UNITS = 100;

  // The epsilon absorbs float noise (345.00000000000006), which would otherwise
  // ceil an already-whole amount up by a full unit.
  const convert = (shopCents) =>
    shopCents <= 0 ? 0 : Math.ceil((shopCents * conversionRate) / CEIL_TO_MINOR_UNITS - 1e-9) * CEIL_TO_MINOR_UNITS;
  const readCents = (element, key) => Number(element.dataset[key]) || 0;

  const numberFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  /**
   * Intl's `style: 'currency'` names the currency the CLDR way ("AED 57.00"),
   * which doesn't match the symbol Liquid's `money` filter uses ("Dhs. 57.00").
   * The active currency's symbol and its placement are read off a `0 | money`
   * sample rendered by the snippet, so JS formats exactly like the Liquid it
   * replaces — the number itself still comes from Intl.
   *
   * @returns {string|null} null when the snippet passed no sample.
   */
  function formatMoney(cents, moneyFormat) {
    if (!moneyFormat) return null;
    const firstDigit = moneyFormat.search(/\d/);
    if (firstDigit === -1) return null;
    const lastDigit = moneyFormat.search(/\d(?=\D*$)/);
    const prefix = moneyFormat.slice(0, firstDigit);
    const suffix = moneyFormat.slice(lastDigit + 1);
    return `${prefix}${numberFormatter.format(cents / 100)}${suffix}`;
  }

  function convertSummary(summary) {
    const moneyFormat = summary.dataset.promoMoneyFormat;
    const shippingCents = convert(readCents(summary, 'promoShipping'));
    const thresholdCents = convert(readCents(summary, 'promoThreshold'));
    const subtotalCents = readCents(summary, 'promoSubtotal');
    const cartTotalCents = readCents(summary, 'promoCartTotal');

    // Same three conditions as the Liquid in cart-summary.liquid.
    const isFree =
      'promoFreeAll' in summary.dataset ||
      shippingCents <= 0 ||
      summary.dataset.promoEmpty === 'true' ||
      (thresholdCents > 0 && subtotalCents >= thresholdCents);

    const shippingValue = summary.querySelector('[data-promo-shipping-value]');
    if (shippingValue) {
      const shippingMoney = formatMoney(shippingCents, moneyFormat);
      if (shippingMoney === null) return;

      shippingValue.textContent = '';
      if (isFree) {
        const freeLabel = document.createElement('span');
        freeLabel.className = 'cart-summary__free';
        freeLabel.textContent = summary.dataset.promoFreeLabel || 'Free';
        shippingValue.appendChild(freeLabel);

        // `shipping_original` is merchant text with a symbol baked in, so it can
        // only be trusted in the currency it was written for — strike through the
        // converted charge instead.
        if (shippingCents > 0) {
          const wasLabel = document.createElement('s');
          wasLabel.className = 'cart-summary__was';
          wasLabel.textContent = shippingMoney;
          shippingValue.appendChild(wasLabel);
        }
      } else {
        shippingValue.textContent = shippingMoney;
      }
    }

    const totalCents = cartTotalCents + (isFree ? 0 : shippingCents);
    const totalMoney = formatMoney(totalCents, moneyFormat);
    if (totalMoney === null) return;
    summary.querySelectorAll('[data-promo-total]').forEach((line) => {
      line.textContent = totalMoney;
    });
  }

  function run() {
    document.querySelectorAll('[data-promo-currency]').forEach(convertSummary);
  }

  run();
  document.addEventListener('cart:update', run);
})();
